/**
 * APTableEmptyState Component
 *
 * Empty state display component for tables with no data. Provides configurable
 * title, description, icon, and action button to guide users.
 *
 * @example
 * ```tsx
 * <APTableEmptyState
 *   title="No users found"
 *   description="There are no users to display. Create a new user to get started."
 *   action={{
 *     label: 'Add User',
 *     onClick: () => console.log('Add user'),
 *     variant: 'primary'
 *   }}
 * />
 * ```
 */

import { forwardRef } from 'react';

import APButton from '@shared/components/atoms/button';
import APTypography from '@shared/components/atoms/typography';

import type { IAPTableEmptyState } from '../../types';

import './styles.scss';

export interface IAPTableEmptyStateProps extends IAPTableEmptyState {
  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Test identifier for testing
   */
  'data-testid'?: string;
}

/**
 * APTableEmptyState - Empty state for tables
 *
 * Displays when table has no data with customizable content,
 * icon, and call-to-action button.
 */
export const APTableEmptyState = forwardRef<HTMLDivElement, IAPTableEmptyStateProps>(
  (
    {
      title = 'No data available',
      description,
      icon,
      content,
      action,
      className = '',
      'data-testid': testId = 'ap-table-empty-state',
      ...rest
    },
    ref
  ) => {
    // Build empty state classes
    const emptyStateClasses = ['ap-table__empty-state', className].filter(Boolean).join(' ');

    // If custom content is provided, use that instead
    if (content) {
      return (
        <div ref={ref} className={emptyStateClasses} data-testid={testId} {...rest}>
          {content}
        </div>
      );
    }

    return (
      <div ref={ref} className={emptyStateClasses} data-testid={testId} {...rest}>
        {/* Icon */}
        {icon && <div className="ap-table__empty-state-icon">{icon}</div>}

        {/* Title */}
        <APTypography
          as="h3"
          size="large"
          weight="500"
          color="base"
          className="ap-table__empty-state-title"
        >
          {title}
        </APTypography>

        {/* Description */}
        {description && (
          <APTypography
            as="p"
            size="base"
            weight="400"
            color="muted"
            className="ap-table__empty-state-description"
          >
            {description}
          </APTypography>
        )}

        {/* Action button */}
        {action && (
          <APButton
            variant={action.variant || 'primary'}
            onClick={action.onClick}
            className="ap-table__empty-state-action"
          >
            {action.label}
          </APButton>
        )}
      </div>
    );
  }
);

APTableEmptyState.displayName = 'APTableEmptyState';

export default APTableEmptyState;
